import React, { FC, Dispatch, SetStateAction } from 'react'
import FlashMessagesConnector, {
  ConnectorProps as FlashMessageProps,
} from 'containers/FlashMessage'
import { Uploader as ArtworkUploader } from 'components/Uploader'
import SortableList from 'components/Artworks/UploadAdditionalImages/SortableList'
import useHook from 'components/Artworks/UploadAdditionalImages/hook'
import { UploadAdditionalImage } from 'components/Artworks/UploadAdditionalImages/styles'
import { uploadStudioArtworkImage, saveStudioArtworkImages } from 'apis/private/uploadStudioArtwork'
import styles from '../aboutArtist/AboutArtist.module.scss'
import { StudioImagesType } from '../../types'

type Props = FlashMessageProps & {
  setShowUploader: Dispatch<SetStateAction<boolean>>
  setStudioImages: Dispatch<SetStateAction<StudioImagesType>>
  additionalImages: StudioImagesType
}

// reusing Sortable List, hook, styles from additional images up-loader
const Component: FC<Props> = (props) => {
  const { additionalImages, addMessage, setShowUploader, setStudioImages } = props
  const { dispatch, onDelete, onSortDown, onSortUp, state, updateDescription } = useHook({
    additionalImages,
  })
  const { files, submitting, uploadId, uploading } = state
  let disabledText = ''

  if (files.length >= 5) {
    disabledText = 'You have reached the max limit of 5 images.'
  } else if (uploading) {
    disabledText = 'Uploading Image'
  }

  return (
    <div className={styles.uploaderSection}>
      <div className={styles.uploaderHeader}>
        <h4>Images Upload</h4>
        <p>
          Uploading your images gives collectors a better sense of you as an artist. Consider
          sharing images such as you in action in your studio, close-up of materials,
          work-in-progress, and more
        </p>
      </div>

      <UploadAdditionalImage className={styles.uploaderImages}>
        <SortableList
          files={files}
          actions={{
            onSortUp,
            onSortDown,
            onDelete,
            updateDescription,
          }}
          app='artist-profile'
        />

        <ArtworkUploader
          onReceiveFile={(file) => {
            dispatch({
              uploading: true,
            })
            uploadStudioArtworkImage({
              file,
              onSuccess: ({ data, s3key }) => {
                const newFile = {
                  s3key,
                  description: null,
                  is_staging: 1,
                  data,
                  order: files.length,
                  id: uploadId + 1,
                  new: true,
                }
                dispatch({
                  files: files.length ? [...files, newFile] : [newFile],
                  uploadId: uploadId + 1,
                  uploading: false,
                })
              },
              onFailure: (payload) => {
                const { errorMessage } = payload
                dispatch({
                  uploading: false,
                })
                addMessage({
                  messages: [errorMessage || 'Problem uploading image'],
                  type: 'error',
                })
              },
            })
          }}
          disabled={files.length >= 5 || submitting || uploading}
          disabledText={disabledText}
        />
      </UploadAdditionalImage>
      <div className={styles.buttonsSection}>
        <button
          className={styles.cancelButton}
          type='button'
          onClick={() => setShowUploader(false)}
        >
          <p className={styles.buttonText}>Cancel</p>
        </button>
        <button
          type='button'
          className={styles.saveButton}
          onClick={() => {
            dispatch({
              submitting: true,
            })
            saveStudioArtworkImages({
              files,
              onSuccess: (payload) => {
                setStudioImages(payload)
                dispatch({
                  submitting: false,
                })
                addMessage({
                  messages: ['You have successfully saved your image.'],
                  type: 'success',
                })
                setShowUploader(false)
              },
              onFailure: (json) => {
                const { message } = json
                dispatch({
                  submitting: false,
                })
                addMessage({
                  messages: [message || 'Problem uploading image'],
                  type: 'error',
                })
              },
            })
          }}
        >
          <p className={styles.buttonText}>{submitting ? 'Saving...' : 'Save Changes'}</p>
        </button>
      </div>
    </div>
  )
}

export default FlashMessagesConnector(Component)
